"use strict";
/**
 * Shared two-step phone → code login controller.
 *
 * THE one implementation of the OTP flow, bound to two surfaces with
 * different element ids: the login page (login-handler.ts) and the
 * session-expiry re-auth modal (modals/reauth-modal.ts). Keep flow
 * changes here so both stay identical.
 *
 * Step "phone": submit sends the code (POST /auth/request-code).
 * Step "code":  submit verifies it (POST /auth/login) — on success the
 *               server session/state updates happen in auth.ts and the
 *               optional onSuccess callback runs (modal close, etc.).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OTP_RESEND_GAP_MS = exports.OTP_CODE_WINDOW_MS = void 0;
exports.setupOtpLoginFlow = setupOtpLoginFlow;
const auth_1 = require("../auth");
const network_error_1 = require("../api/network-error");
const ui_1 = require("../ui");
/** Mirrors the server's window (otp-policy.ts); display/pacing only. */
exports.OTP_CODE_WINDOW_MS = 2 * 60 * 1000;
exports.OTP_RESEND_GAP_MS = 30 * 1000;
/**
 * Wire the two-step flow onto existing DOM. Safe no-op if the form
 * isn't in the document.
 */
function setupOtpLoginFlow(ids, onSuccess) {
    const form = document.getElementById(ids.form);
    if (!form)
        return;
    const el = (id) => document.getElementById(id);
    const phoneStep = el(ids.phoneStep);
    const phoneInput = el(ids.phoneInput);
    const codeStep = el(ids.codeStep);
    const codeInput = el(ids.codeInput);
    const submitButton = el(ids.submitButton);
    const resendButton = el(ids.resendButton);
    const changeNumberButton = el(ids.changeNumberButton);
    const statusEl = el(ids.status);
    if (!phoneStep ||
        !phoneInput ||
        !codeStep ||
        !codeInput ||
        !submitButton ||
        !resendButton ||
        !changeNumberButton ||
        !statusEl) {
        console.error("[OtpLogin] Missing elements for form", ids.form);
        return;
    }
    let step = "phone";
    let phone = "";
    let codeSentAt = 0;
    let ticker = null;
    let busy = false;
    function setStep(next) {
        step = next;
        phoneStep.classList.toggle("hidden", next !== "phone");
        codeStep.classList.toggle("hidden", next !== "code");
        submitButton.textContent = next === "phone" ? "Send Code" : "Log In";
        if (next === "code") {
            codeInput.focus();
        }
    }
    function stopTicker() {
        if (ticker !== null) {
            window.clearInterval(ticker);
            ticker = null;
        }
    }
    function tick() {
        const now = Date.now();
        const expiresInMs = codeSentAt + exports.OTP_CODE_WINDOW_MS - now;
        const resendInMs = codeSentAt + exports.OTP_RESEND_GAP_MS - now;
        resendButton.disabled = resendInMs > 0;
        resendButton.textContent =
            resendInMs > 0
                ? `Resend in ${Math.ceil(resendInMs / 1000)}s`
                : "Resend code";
        if (expiresInMs <= 0) {
            statusEl.textContent = "Code expired — send a new one.";
            stopTicker();
            return;
        }
        const totalSeconds = Math.ceil(expiresInMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        statusEl.textContent = `Code expires in ${minutes}:${String(seconds).padStart(2, "0")}`;
    }
    async function sendCode() {
        const result = await (0, auth_1.requestOtpCode)(phone);
        if (!result.sent) {
            (0, ui_1.showError)(result.error || "Could not send code", ids.error);
            return false;
        }
        (0, ui_1.hideError)(ids.error);
        codeSentAt = Date.now();
        codeInput.value = "";
        stopTicker();
        ticker = window.setInterval(tick, 1000);
        tick();
        return true;
    }
    function resetToPhoneStep() {
        stopTicker();
        codeInput.value = "";
        statusEl.textContent = "";
        (0, ui_1.hideError)(ids.error);
        setStep("phone");
    }
    form.addEventListener("submit", async (e) => {
        e.preventDefault();
        if (busy)
            return;
        busy = true;
        submitButton.disabled = true;
        try {
            if (step === "phone") {
                phone = phoneInput.value.trim();
                if (!phone) {
                    (0, ui_1.showError)("Enter your phone number", ids.error);
                    return;
                }
                if (await sendCode()) {
                    setStep("code");
                }
            }
            else {
                const code = codeInput.value.trim();
                if (!/^\d{4,10}$/.test(code)) {
                    (0, ui_1.showError)("Enter the 6-digit code from the SMS", ids.error);
                    return;
                }
                // A NetworkError here means the request never reached the
                // server. Reporting it as a bad code would send the operator
                // chasing the wrong problem and burn OTP attempts against the
                // rate limit.
                let authenticated;
                try {
                    authenticated = await (0, auth_1.authenticateWithOtp)(phone, code);
                }
                catch (error) {
                    (0, ui_1.showError)((0, network_error_1.isNetworkError)(error)
                        ? error.message
                        : "Login failed. Please try again.", ids.error);
                    return;
                }
                if (authenticated) {
                    resetToPhoneStep();
                    phoneInput.value = "";
                    onSuccess?.();
                }
                else {
                    (0, ui_1.showError)("Invalid or expired code. Please try again.", ids.error);
                }
            }
        }
        finally {
            busy = false;
            submitButton.disabled = false;
        }
    });
    resendButton.addEventListener("click", async () => {
        if (busy || resendButton.disabled)
            return;
        busy = true;
        try {
            await sendCode();
        }
        finally {
            busy = false;
        }
    });
    changeNumberButton.addEventListener("click", () => {
        if (busy)
            return;
        resetToPhoneStep();
        phoneInput.focus();
    });
}
